// src/pages/HubReturn.js
//
// Where Xiva Login (the hub) sends someone who picked RegX:
// /hub-return#pass=<one-time pass>. The pass is read once, removed from the
// address bar, and handed to the hubSignIn function, which redeems it with the
// hub server to server and (for an existing login with access to the hub's
// company + site) writes the verified 2-step session and answers a sign-in
// token. The hub's company/site becomes the active one here.
//
// Anyone RegX doesn't sign in automatically is simply sent to the normal login.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithCustomToken, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { FUNCTIONS_BASE } from "../config/functionsBase";
import { deriveAccess, normalizeMembership, effectiveRole } from "../services/membershipService";
import { setActiveTenantSite } from "../services/activeTenant";
import { roleHome } from "../utils/roleHome";

export const readPass = (hash) => {
  const match = /(?:^#|&)pass=([A-Za-z0-9_-]{43})(?:&|$)/.exec(hash || "");
  return match ? match[1] : null;
};

const MESSAGES = {
  disabled: "Your RegX login is switched off. Ask an admin.",
  invalid: "That sign-in link has expired or was already used. Open RegX from Xiva Login again.",
  unavailable: "Something went wrong. Try again in a moment.",
};

const HubReturn = () => {
  const navigate = useNavigate();
  const started = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (started.current) return; // a pass works once
    started.current = true;

    const pass = readPass(window.location.hash);
    window.history.replaceState(null, "", window.location.pathname);
    if (!pass) {
      setError(MESSAGES.invalid);
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${FUNCTIONS_BASE}/hubSignIn`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pass }),
        });
        const body = await res.json().catch(() => ({}));
        if (body.error === "not_allowed") {
          await signOut(auth).catch(() => {});
          navigate("/login", { replace: true });
          return;
        }
        if (!res.ok || !body.token) {
          setError(MESSAGES[body.error] || MESSAGES.unavailable);
          return;
        }

        const cred = await signInWithCustomToken(auth, body.token);
        setActiveTenantSite(body.companyId, body.siteId);
        const roleSnap = await getDoc(doc(db, "user_roles", (cred.user.email || "").toLowerCase()));
        const access = deriveAccess(normalizeMembership(roleSnap.exists() ? roleSnap.data() : null));
        navigate(roleHome(effectiveRole(access, body.companyId, body.siteId)), { replace: true });
      } catch (err) {
        console.error("HUB SIGN-IN ERROR:", err);
        setError(MESSAGES.unavailable);
      }
    })();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0b0820] text-slate-300 text-sm p-6">
      {error ? (
        <div className="max-w-sm text-center space-y-3">
          <p className="text-base font-semibold text-white">Couldn't sign you in</p>
          <p>{error}</p>
          <a className="underline" href="/login">Go to the RegX login</a>
        </div>
      ) : (
        "Signing you in…"
      )}
    </div>
  );
};

export default HubReturn;
